import { FormattedMessage } from 'react-intl';
import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { Box, CircularProgress, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useDataProvider,
  Select,
  FieldWrapperVertical,
  TextField,
  useUserAccess,
  SnackbarApiErrorMessage,
  useLocalization,
  useDialog,
  useSnackbar,
} from '@ui-framework/index';
import { nanoid } from 'nanoid';
import DialogCloseButton from './DialogCloseButton';
import IconLibrary from '../IconLibrary';

function RejectJobDialog({ open, onClose, jobId, dispatchEventId, jobNumber }) {
  const { formatMessage } = useLocalization();

  const validationschema = yup.object().shape({
    rejectReason: yup.string().required(
      formatMessage({
        id: 'REJECT_REASON_IS_REQUIRED',
        defaultMessage: 'Reject reason is required',
      }),
    ),
    otherReason: yup.string().when('rejectReason', {
      is: 'other',
      then: schema =>
        schema.required(
          formatMessage({
            id: 'PLEASE_SPECIFY_A_REASON',
            defaultMessage: 'Please specify a reason',
          }),
        ),
      otherwise: schema => schema.notRequired(),
    }),
  });

  const rejectReasons = [
    {
      value: 'No Availability',
      label: formatMessage({
        id: 'NO_AVAILABILITY',
        defaultMessage: 'No Availability',
      }),
    },
    {
      value: 'Out of Service Area',
      label: formatMessage({
        id: 'OUT_OF_SERVICE_AREA',
        defaultMessage: 'Out of Service Area',
      }),
    },
    {
      value: "Don't service the Product Line",
      label: formatMessage({
        id: 'DONT_SERVICE_THE_PRODUCT_LINE',
        defaultMessage: "Don't service the Product Line",
      }),
    },
    {
      value: "Don't service the Brand",
      label: formatMessage({
        id: 'DONT_SERVICE_THE_BRAND',
        defaultMessage: "Don't service the Brand",
      }),
    },
    {
      value: 'other',
      label: formatMessage({ id: 'OTHER', defaultMessage: 'Other' }),
    },
  ];

  const methods = useForm({
    resolver: yupResolver(validationschema),
    defaultValues: {
      rejectReason: '',
    },
  });

  const {
    handleSubmit,
    register,
    watch,
    formState: { errors },
  } = methods;

  const userAccess = useUserAccess();
  const userID = userAccess?.company?.id;
  const snackbar = useSnackbar();
  const { confirmationRef } = useDialog();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getApiUrl, put, get } = useDataProvider(provider);

  const [loading, setLoading] = useState(false);

  const buttons = [
    {
      children: formatMessage({ id: 'REJECT', defaultMessage: 'Reject' }),
      type: 'Submit',
      variant: 'contained',
      color: 'primary',
      startIcon: <IconLibrary.Reject />,
    },
    {
      children: formatMessage({ id: 'CANCEL', defaultMessage: 'Cancel' }),
      onClick: () => {
        onClose(null);
      },
      variant: 'contained',
      color: 'secondary',
      startIcon: <IconLibrary.Cancel />,
    },
  ];

  const selectedReason = watch('rejectReason');

  useEffect(() => {
    if (open) {
      methods.reset();
    }
  }, [open, jobId]);

  const getJobDispatchId = async () => {
    const response = await get(
      `${getApiUrl()}/sxcl/v1/service-providers/${userID}/pending-acceptance/${jobId}`,
    );
    if (response.ok) {
      return response?.data?.dispatchEvent?.id;
    }
    return null;
  };

  async function onFormSubmit(data) {
    setLoading(true);
    const dispatchEventValue = dispatchEventId || (await getJobDispatchId());
    if (!dispatchEventValue) {
      snackbar.showMessage({
        type: 'error',
        data: formatMessage({
          id: 'ERROR_COULD_NOT_REJECT_THE_JOB',
          defaultMessage: 'Error: Could not reject the job',
        }),
      });
    } else {
      const reasonToReject =
        data.rejectReason === 'other' ? data.otherReason : data.rejectReason;
      const body = {
        dispatchEventId: dispatchEventValue,
        reason: reasonToReject,
      };
      const response = await put(
        `${getApiUrl()}/sxcl/v1/service-providers/${userID}/pending-acceptance/${jobId}/reject`,
        body,
      );
      setLoading(false);
      if (response.ok) {
        confirmationRef.current.init({
          message: 'Job was rejected successfully',
          positiveButtonText: 'Close',
          width: 360,
          title: 'Job Rejected',
          onChange: () => {
            confirmationRef.current.setOpen(false);
            onClose(nanoid());
          },
        });
        confirmationRef.current.setOpen(true);
      } else {
        snackbar.showMessage({
          type: 'error',
          data: <SnackbarApiErrorMessage errors={response?.errors} />,
        });
      }
    }
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose(null);
      }}
      maxWidth="sm"
      fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconLibrary.RejectServiceJob />
        {formatMessage({
          id: 'REJECT_JOB',
          defaultMessage: 'Reject Job',
        })}
        &nbsp;&ndash;
        <Typography color="primary" variant="body">
          {jobNumber}
        </Typography>
        <DialogCloseButton
          disabled={loading}
          onClose={() => {
            onClose(null);
          }}
        />
      </DialogTitle>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <DialogContent>
            {loading ? (
              <Box textAlign="center">
                <CircularProgress size="30px" />
                <Typography>
                  <FormattedMessage
                    id="PROCESSING"
                    defaultMessage="Processing"
                  />
                </Typography>
              </Box>
            ) : (
              <>
                <Box>
                  <Typography
                    textAlign="center"
                    sx={{ mb: 2, fontSize: '1.2rem' }}>
                    {formatMessage({
                      id: 'REJECT_MESSAGE_JOB',
                      defaultMessage: `Are you sure you want to Reject Job ${jobNumber}?`,
                    })}
                  </Typography>
                </Box>
                <FieldWrapperVertical
                  field={{
                    name: 'rejectReason',
                    label: {
                      localeId: 'REJECT_REASON',
                      defaultMessage: 'Reject Reason',
                    },
                    required: true,
                  }}
                  errors={errors}>
                  <Select
                    name="rejectReason"
                    options={rejectReasons}
                    error={!!errors?.rejectReason}
                    helperText={errors?.rejectReason?.message}
                  />
                </FieldWrapperVertical>
                {selectedReason === 'other' && (
                  <Box sx={{ mt: 2 }}>
                    <FieldWrapperVertical
                      field={{
                        name: 'otherReason',
                        label: {
                          localeId: 'PLEASE_SPECIFY',
                          defaultMessage: 'Please specify',
                        },
                        required: true,
                      }}
                      errors={errors}>
                      <TextField
                        {...register('otherReason')}
                        error={!!errors?.otherReason}
                        helperText={errors?.otherReason?.message}
                      />
                    </FieldWrapperVertical>
                  </Box>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions
            display="flex"
            sx={{ justifyContent: 'center', my: 2 }}>
            {buttons.map(btn => {
              const { children, ...rest } = btn;
              return (
                <Button key={children} disabled={loading} {...rest}>
                  {children}
                </Button>
              );
            })}
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
}

export default RejectJobDialog;
